/* Koukita – custom storefront behaviour (vanilla JS, no dependencies). */
(() => {
  const STORAGE_KEY = 'koukita-locale-choice';

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (e) {
        /* storage unavailable (private mode) – ignore */
      }
    },
  };

  /* ---------------------------------------------------------------
   * Language selector: the header dropdown and mobile drawer expect
   * focusSearchInput() / resetForm() on the element with ref="localizationForm".
   * ------------------------------------------------------------- */
  class KoukitaLocaleSelector extends HTMLElement {
    connectedCallback() {
      this.addEventListener('submit', this.#onSubmit);
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.#onSubmit);
    }

    focusSearchInput = () => {
      const target = this.querySelector('[aria-current="true"]') || this.querySelector('button');
      target?.focus({ preventScroll: true });
    };

    resetForm = () => {};

    #onSubmit = (event) => {
      const button = event.target.querySelector('[data-locale-option]');
      if (button) storage.set(STORAGE_KEY, button.dataset.localeOption);
    };
  }

  if (!customElements.get('koukita-locale-selector')) {
    customElements.define('koukita-locale-selector', KoukitaLocaleSelector);
  }

  /* ---------------------------------------------------------------
   * Language suggestion (never forced; dismissible; remembered).
   * ------------------------------------------------------------- */
  const SUGGEST_COPY = {
    fr: { text: 'Voir ce site en français ?', accept: 'Oui, continuer', dismiss: 'Non merci', lang: 'fr' },
    'nl-be': { text: 'Deze website bekijken in het Nederlands (België)?', accept: 'Ja, graag', dismiss: 'Nee, bedankt', lang: 'nl-BE' },
    'nl-nl': { text: 'Deze website bekijken in het Nederlands?', accept: 'Ja, graag', dismiss: 'Nee, bedankt', lang: 'nl-NL' },
    de: { text: 'Diese Website auf Deutsch ansehen?', accept: 'Ja, gerne', dismiss: 'Nein, danke', lang: 'de' },
  };

  /**
   * Picks the best storefront variant from detected country + browser languages.
   * @param {string|null} country
   * @param {readonly string[]} languages
   * @returns {string|null}
   */
  function pickVariant(country, languages) {
    const langs = (languages || []).map((l) => String(l).toLowerCase());
    const primary = langs[0] ? langs[0].slice(0, 2) : '';
    const has = (code) => langs.some((l) => l.slice(0, 2) === code);

    switch (country) {
      case 'BE':
        if (primary === 'fr') return 'fr';
        if (primary === 'de') return 'de';
        return 'nl-be';
      case 'NL':
        return 'nl-nl';
      case 'FR':
      case 'LU':
      case 'MC':
        return primary === 'de' ? 'de' : 'fr';
      case 'DE':
      case 'AT':
      case 'LI':
        return 'de';
      case 'CH':
        if (primary === 'fr') return 'fr';
        return 'de';
      default:
        if (primary === 'fr') return 'fr';
        if (primary === 'de') return 'de';
        if (primary === 'nl') return langs[0].includes('-be') ? 'nl-be' : 'nl-nl';
        if (has('fr')) return 'fr';
        return null;
    }
  }

  class KoukitaLocaleSuggest extends HTMLElement {
    async connectedCallback() {
      const current = this.dataset.current;
      const remembered = storage.get(STORAGE_KEY);
      if (remembered) return; // the customer already chose (or dismissed) – respect it

      let country = null;
      try {
        const res = await fetch('/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (res.ok) {
          const data = await res.json();
          country = data?.detected_values?.country?.handle || null;
        }
      } catch (e) {
        /* network issue – fall back to browser language only */
      }

      const suggestion = pickVariant(country, navigator.languages || [navigator.language]);
      if (!suggestion || suggestion === current || !SUGGEST_COPY[suggestion]) return;

      const optionButton = this.querySelector(`[data-locale-option="${suggestion}"]`);
      if (!optionButton) return; // market not available

      const copy = SUGGEST_COPY[suggestion];
      const text = this.querySelector('[data-suggest-text]');
      const accept = this.querySelector('[data-suggest-accept]');
      const dismiss = this.querySelector('[data-suggest-dismiss]');
      if (!text || !accept || !dismiss) return;

      text.textContent = copy.text;
      text.lang = copy.lang;
      accept.textContent = copy.accept;
      accept.lang = copy.lang;
      dismiss.textContent = copy.dismiss;
      dismiss.lang = copy.lang;

      accept.addEventListener('click', () => {
        storage.set(STORAGE_KEY, suggestion);
        const form = optionButton.closest('form');
        if (form && typeof form.requestSubmit === 'function') form.requestSubmit(optionButton);
        else optionButton.click();
      });
      dismiss.addEventListener('click', () => {
        storage.set(STORAGE_KEY, current || 'dismissed');
        this.hidden = true;
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') dismiss.click();
      });

      this.hidden = false;
    }
  }

  if (!customElements.get('koukita-locale-suggest')) {
    customElements.define('koukita-locale-suggest', KoukitaLocaleSuggest);
  }

  /* ---------------------------------------------------------------
   * Free-shipping notice: refresh after cart changes.
   * ------------------------------------------------------------- */
  function formatMoney(cents, format) {
    const value = Number(cents) / 100;
    const fmt = format || '€{{amount_with_comma_separator}}';
    const groups = (n, dec, thousands, decimal) => {
      const fixed = n.toFixed(dec);
      const [int, frac] = fixed.split('.');
      const intFmt = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${intFmt}${decimal}${frac}` : intFmt;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return groups(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return groups(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return groups(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return groups(value, 2, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return groups(value, 2, "'", '.');
        default:
          return groups(value, 2, ',', '.');
      }
    });
  }

  async function refreshCartNotices() {
    const notices = document.querySelectorAll('koukita-cart-notice');
    if (!notices.length) return;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      notices.forEach((notice) => notice.update(cart.total_price));
    } catch (e) {
      /* ignore */
    }
  }

  class KoukitaCartNotice extends HTMLElement {
    update(total) {
      const threshold = Number(this.dataset.threshold || 0);
      if (!threshold) return;
      const remaining = threshold - Number(total || 0);
      const text = this.querySelector('.koukita-cart-notice__text');
      const bar = this.querySelector('.koukita-cart-notice__bar span');
      if (text) {
        text.textContent =
          remaining <= 0
            ? this.dataset.msgReached
            : (this.dataset.msgRemaining || '').replace('[amount]', formatMoney(remaining, this.dataset.moneyFormat));
      }
      if (bar) bar.style.width = `${Math.max(0, Math.min(100, (Number(total || 0) / threshold) * 100))}%`;
    }
  }

  if (!customElements.get('koukita-cart-notice')) {
    customElements.define('koukita-cart-notice', KoukitaCartNotice);
  }

  document.addEventListener('shopify:cart:lines-update', () => window.setTimeout(refreshCartNotices, 150));

  /* ---------------------------------------------------------------
   * Dialogs (size guide).
   * ------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-koukita-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-koukita-dialog-open'));
      if (dialog instanceof HTMLDialogElement) {
        dialog.showModal();
        dialog.addEventListener('close', () => opener.focus({ preventScroll: true }), { once: true });
      }
      return;
    }

    if (target.closest('[data-koukita-dialog-close]')) {
      target.closest('dialog')?.close();
      return;
    }

    // Click on the backdrop closes the dialog
    if (target instanceof HTMLDialogElement && target.classList.contains('koukita-dialog')) {
      const rect = target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) target.close();
    }
  });
})();